<template>
  <PageWrapper
    :active-page="PAGE_COMMUNITY_COMMUNITY"
    :active-section="SECTION_COMMUNITY"
    :breadcrumbs="breadcrumbs"
    align="left"
    page-name="Call"
    sub-title=""
    :title="orgName"
    width="fixed"
  >
    <div ref="videoContainer" class="video-container" @mousemove="handleMouseMove">
      <video
        v-if="remoteStream"
        ref="remoteVideo"
        class="main-video"
        autoplay
      />
      <video
        ref="localVideo"
        disablePictureInPicture
        :class="remoteStream ? 'mini-video' : 'main-video'"
        autoplay
        muted
        @mousedown="startDrag"
      />

      <iRow
        ref="controls"
        class="controls-overlay"
        align="evenly"
        :width="200"
        vertical-align="middle"
      >
        <template v-if="controlsVisible">
          <iButton variant="tertiary" @click="toggleCamera">
            <iIcon variant="secondary" :icon="cameraEnabled ? 'camera' : 'camera-slash'" />
          </iButton>
          <iButton variant="tertiary" @click="toggleMicrophone">
            <iIcon variant="secondary" :icon="microphoneEnabled ? 'microphone' : 'microphone-slash'" />
          </iButton>
          <iButton v-if="isCaller" variant="tertiary" @click="leaveCall">
            <iIcon variant="secondary" icon="arrow-right-from-bracket" />
          </iButton>
          <iButton v-if="!isCaller" variant="tertiary" @click="startCall">
            <iIcon variant="secondary" icon="door-open" />
          </iButton>
        </template>
      </iRow>
    </div>
  </PageWrapper>
</template>

<script>
import PageWrapper from "@/components/PageWrapper.vue";
import { PAGE_COMMUNITY_COMMUNITY, SECTION_COMMUNITY } from "@/constants/nav-constants";
import { useOrganizationStore } from "@/stores/organization-store";
import { mapState } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { useConversationStore } from "%/stores/conversation-store";

export default {
  // eslint-disable no-console
  name: "DirectVideoPage",
  components: { PageWrapper },
  props: {
    roomIdentifier: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      SECTION_COMMUNITY,
      PAGE_COMMUNITY_COMMUNITY,
      breadcrumbs: [
        { url: "/", label: "Home" },
        { url: "/community", label: "Community" },
        { url: `/community/org/${this.orgId}/profile`, label: useOrganizationStore().orgName },
      ],
      localStream: null,
      remoteStream: null,
      peerConnection: null,
      signalingSocket: null,
      isCaller: false,
      cameraEnabled: true,
      microphoneEnabled: true,
      controlsVisible: true,
      hideControlsTimeout: null,
      dragging: false,
      dragOffset: { x: 0, y: 0 },
    };
  },
  computed: {
    ...mapState(useConversationStore, ["conversationId"]),
    ...mapState(useOrganizationStore, ["orgName"]),
    ...mapState(useGeneralStore, ["userId"]),

  },
  mounted() {
    this.setupWebSocket();
    this.getUserMedia();

    document.addEventListener("mouseup", this.stopDrag);
    document.addEventListener("mousemove", this.onDrag);
  },
  beforeUnmount() {
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("mousemove", this.onDrag);
  },
  methods: {
    handleMouseMove() {
      this.controlsVisible = true;
      this.resetHideControlsTimeout();
    },
    resetHideControlsTimeout() {
      clearTimeout(this.hideControlsTimeout);
      this.hideControlsTimeout = setTimeout(() => {
        this.controlsVisible = false;
      }, 3000);
    },
    startDrag(event) {
      if (!this.remoteStream) {
        return;
      }
      this.dragging = true;
      const video = this.$refs.localVideo;
      const videoClientRect = video.getBoundingClientRect();
      this.dragOffset.x = event.clientX - videoClientRect.left;
      this.dragOffset.y = event.clientY - videoClientRect.top;
    },
    onDrag(event) {
      if (!this.dragging) {
        return;
      }
      const container = this.$refs.videoContainer;
      const video = this.$refs.localVideo;
      const containerClientRect = container.getBoundingClientRect();

      let left = event.clientX - containerClientRect.left - this.dragOffset.x;
      let top = event.clientY - containerClientRect.top - this.dragOffset.y;

      left = Math.max(0, Math.min(left, containerClientRect.width - video.offsetWidth));
      top = Math.max(0, Math.min(top, containerClientRect.height - video.offsetHeight));

      video.style.left = `${left}px`;
      video.style.top = `${top}px`;
    },
    stopDrag() {
      this.dragging = false;
    },
    setupWebSocket() {
      this.signalingSocket = new WebSocket("wss://creator-ws.inspiration.co:9090");

      this.signalingSocket.onopen = () => {
        // eslint-disable-next-line no-console
        console.log("Connected to signaling server.");
      };

      this.signalingSocket.onmessage = event => {
        const message = JSON.parse(event.data);
        if (message.offer) {
          this.handleOffer(message.offer);
        } else if (message.answer) {
          this.handleAnswer(message.answer);
        } else if (message.iceCandidate) {
          this.handleNewICECandidate(message.iceCandidate);
        } else if (message.peerDisconnected) {
          this.handlePeerDisconnected();
        }
      };
    },
    handlePeerDisconnected() {
      if (this.remoteStream) {
        this.remoteStream.getTracks().forEach(track => track.stop());
        this.remoteStream = null;
      }
    },
    toggleCamera() {
      if (this.cameraEnabled) {
        const videoTrack = this.localStream.getTracks().find(track => track.kind === "video");
        if (videoTrack) {
          videoTrack.enabled = false;
        }
      } else {
        this.getUserMedia();
      }
      this.cameraEnabled = !this.cameraEnabled;
    },
    toggleMicrophone() {
      if (this.microphoneEnabled) {
        const audioTrack = this.localStream.getTracks().find(track => track.kind === "audio");
        if (audioTrack) {
          audioTrack.enabled = false;
        }
      } else {
        const audioTrack = this.localStream.getTracks().find(track => track.kind === "audio");
        if (audioTrack) {
          audioTrack.enabled = true;
        }
      }

      this.microphoneEnabled = !this.microphoneEnabled;
    },
    startCall() {
      this.isCaller = true;
      this.getUserMedia();
    },
    getUserMedia() {
      navigator.mediaDevices.getUserMedia({ video: true, audio: true })
          .then(stream => {
            this.localStream = stream;
            this.$refs.localVideo.srcObject = this.localStream;
            if (this.isCaller) {
              this.createPeerConnection();
              this.createOffer();
            }
          })
          // eslint-disable-next-line no-console
          .catch(err => console.error("Error accessing media devices.", err));
    },
    createPeerConnection() {
      // @TODO - this will use dynamically generated temp credentials that we add on page load and attach to the page
      // This is just WIP to get it live without sucking too much time, but not a good idea.
      // Future me may regret this decision...
      const configuration = {
        iceServers: [
          {
            urls: "turn:turn.inspiration.co:3478",
            username: "test",
            credential: "Test@123",
          },
        ],
      };
      this.peerConnection = new RTCPeerConnection(configuration);

      this.peerConnection.onicecandidate = event => {
        // if (!this.peerConnection) {
        //   return;
        // }
        if (event.candidate) {
          this.signalingSocket.send(JSON.stringify({ iceCandidate: event.candidate, roomId: this.roomIdentifier }));
        }
      };

      this.peerConnection.ontrack = event => {
        // if (!this.isCaller) {
        //   return;
        // }
        this.remoteStream = event.streams[0];
        if (this.$refs.remoteVideo) {
          this.$refs.remoteVideo.srcObject = this.remoteStream;
        }
      };

      this.localStream.getTracks().forEach(track => {
        this.peerConnection.addTrack(track, this.localStream);
      });
    },
    createOffer() {
      this.peerConnection.createOffer()
          .then(offer => {
            return this.peerConnection.setLocalDescription(offer);
          })
          .then(() => {
            this.signalingSocket.send(JSON.stringify({
              offer: this.peerConnection.localDescription,
              roomId: this.roomIdentifier,
            }));
          })
          // eslint-disable-next-line no-console
          .catch(err => console.error("Error creating offer:", err));
    },
    handleOffer(offer) {
      this.createPeerConnection();
      this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer))
          .then(() => this.peerConnection.createAnswer())
          .then(answer => this.peerConnection.setLocalDescription(answer))
          .then(() => {
            this.signalingSocket.send(JSON.stringify({
              answer: this.peerConnection.localDescription,
              roomId: this.roomIdentifier,
            }));
          })
          // eslint-disable-next-line no-console
          .catch(err => console.error("Error handling offer:", err));
    },
    handleAnswer(answer) {
      this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer))
          // eslint-disable-next-line no-console
          .catch(err => console.error("Error setting remote description:", err));
    },
    handleNewICECandidate(candidate) {
      // if (!this.peerConnection) {
      //   return;
      // }
      this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate))
          // eslint-disable-next-line no-console
          .catch(err => console.error("Error adding ICE candidate:", err));
    },
    leaveCall() {
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      this.signalingSocket.send(JSON.stringify({
        peerDisconnected: true,
        roomId: this.roomIdentifier,
      }));

      this.isCaller = false;
      this.remoteStream = null;

      if (this.signalingSocket) {
        this.signalingSocket.close();
      }
      this.setupWebSocket();

      if (this.$refs.remoteVideo) {
        this.$refs.remoteVideo.srcObject = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.video-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background-color: #000;
  overflow: hidden;
  border-radius: 8px;
}

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  border-radius: 8px;
}

.main-video {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
}

.mini-video {
  position: absolute;
  width: 25%;
  height: 25%;
  aspect-ratio: 16/9;
  top: 16px;
  right: 16px;
  border-radius: 8px;
  z-index: 2;
  cursor: move;
}

.controls-overlay {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  gap: 12px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  transition: opacity 0.3s ease;
  opacity: 1;
  pointer-events: auto;
  min-height: 8px;

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
}
</style>
